import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { FavouritesSportIcon } from "./FavouritesSportIcon";

export type ProfileFavChip = { key: string; label: string; longLabel?: string };

type Props = { chips: readonly ProfileFavChip[] };

/** Cricketer headshots + generic cricket/football glyphs get the padded tile; sports rasters + team crests stay flush on the strip. */
function favChipUsesTintedTile(chipKey: string): boolean {
  if (chipKey.startsWith("cr-")) return true;
  if (chipKey.startsWith("team-")) return true;
  if (chipKey === "tour") return true;
  if (chipKey === "t1" || chipKey === "t2") return true;
  return false;
}

/**
 * Horizontal favourites row (30×30 chips); tap toggles a readable label below.
 */
export function ProfileFavouritesStrip({ chips }: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const expanded = expandedKey ? chips.find((c) => c.key === expandedKey) : undefined;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [chips, updateOverflow]);

  return (
    <section className="mb-4">
      <div className="flex flex-col items-center justify-center gap-2 rounded-none bg-[#652DAF] px-3 py-2.5">
        <p className="text-center font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
          Favourites
        </p>
        <div
          ref={scrollRef}
          className="hide-scrollbar w-full min-w-0 max-w-full shrink-0 overflow-x-auto overflow-y-hidden"
        >
          <div
            className={`flex min-w-full flex-nowrap items-center gap-2.5 ${
              overflowing ? "justify-start" : "justify-center"
            }`}
          >
            {chips.map((c) => {
              const isOpen = expandedKey === c.key;
              const tinted = favChipUsesTintedTile(c.key);
              return (
                <button
                  type="button"
                  key={c.key}
                  onClick={() => setExpandedKey((k) => (k === c.key ? null : c.key))}
                  className={`box-border flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded-none p-0 leading-none text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40 ${
                    tinted
                      ? isOpen
                        ? "border border-white/35 bg-white/20"
                        : "border border-transparent bg-white/10 hover:bg-white/15"
                      : isOpen
                        ? "ring-1 ring-inset ring-white/45"
                        : ""
                  }`}
                  aria-expanded={isOpen}
                  aria-label={c.longLabel ?? c.label}
                >
                  <FavouritesSportIcon
                    chipKey={c.key}
                    label={c.label}
                    size={tinted ? "stripTinted" : "stripFlat"}
                  />
                </button>
              );
            })}
          </div>
        </div>
        {expanded ? (
          <p className="w-full max-w-full break-words px-1 text-center font-anton text-[13px] font-normal uppercase leading-snug tracking-normal text-white [leading-trim:none]">
            {expanded.longLabel ?? expanded.label}
          </p>
        ) : null}
      </div>
    </section>
  );
}
